import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { listLlmBatchJobs, type LlmBatchJob, type LlmBatchStatus } from "@/api/llm";

export default function AdminLlmJobs() {
  const [params, setParams] = useSearchParams();
  const statusFilter = params.get("status") as LlmBatchStatus | null;

  const jobsQ = useQuery({
    queryKey: ["llm-batch-jobs", statusFilter],
    queryFn: async () => {
      const r = await listLlmBatchJobs({
        status: statusFilter ?? undefined,
        limit: 100,
      });
      if (!r.ok) throw new Error(r.error);
      return r.data;
    },
    refetchInterval: 30_000,
  });

  const jobs = jobsQ.data?.batch_jobs ?? [];

  return (
    <div className="rf-shell">
      <main className="rf-main">
        <div className="rf-content">
          <header className="rf-head">
            <h1>LLM Batch Jobs</h1>
          </header>
          <div className="rf-section">
            <div className="rf-section__head">
              <span>Фильтр</span>
              <span className="mt-toolbar__sep" style={{ flex: 1 }} />
              <select
                value={statusFilter ?? ""}
                onChange={(e) => {
                  const next = new URLSearchParams(params);
                  if (e.target.value) next.set("status", e.target.value);
                  else next.delete("status");
                  setParams(next);
                }}
                className="mt-llm__select"
              >
                <option value="">все статусы</option>
                <option value="pending">pending</option>
                <option value="in_progress">in_progress</option>
                <option value="completed">completed</option>
                <option value="failed">failed</option>
                <option value="expired">expired</option>
                <option value="cancelled">cancelled</option>
              </select>
            </div>
          </div>

          <div className="rf-section">
            <div className="rf-section__head">
              <span>Jobs</span>
              <span className="mt-toolbar__sep" style={{ flex: 1 }} />
              <span className="mt-text-tight mt-muted">
                {jobs.length}
              </span>
            </div>
            <div className="rf-section__body">
              {jobsQ.isLoading && <div className="mt-llm-empty">Загружаем…</div>}
              {!jobsQ.isLoading && jobs.length === 0 && (
                <div className="mt-llm-empty">Нет batch-задач.</div>
              )}
              {jobs.map((job) => (
                <JobRow key={job.batch_job_id} job={job} />
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function JobRow({ job }: { job: LlmBatchJob }) {
  return (
    <Link
      to={`/admin/llm-jobs/${encodeURIComponent(job.batch_job_id)}`}
      className="mt-llm-card"
      style={{ display: "flex", textDecoration: "none", color: "inherit" }}
    >
      <div style={{ flex: 1 }}>
        <div className="mt-llm-card__head">
          <span className={badgeClassForStatus(job.status)}>{job.status}</span>
          <span className="mt-mono mt-text-tight">{job.batch_job_id.slice(0, 8)}</span>
          <span>·</span>
          <span>{job.model}</span>
          <span>·</span>
          <span>{job.item_count} items</span>
          <span>·</span>
          <span>{job.requested_by}</span>
        </div>
        <div className="mt-llm-card__reason">
          {job.provider_batch_id ? `provider: ${job.provider_batch_id}` : "—"}
          {" · "}
          создан {relTime(job.created_at)}
          {job.completed_at && ` · завершён ${relTime(job.completed_at)}`}
        </div>
      </div>
    </Link>
  );
}

function badgeClassForStatus(status: LlmBatchStatus): string {
  if (status === "completed") return "mt-badge mt-badge--success mt-badge--no-dot";
  if (status === "failed" || status === "expired") {
    return "mt-badge mt-badge--error mt-badge--no-dot";
  }
  if (status === "cancelled") return "mt-badge mt-badge--neutral mt-badge--no-dot";
  return "mt-badge mt-badge--info mt-badge--no-dot";
}

function relTime(iso: string): string {
  try {
    const d = new Date(iso);
    const diffSec = Math.round((Date.now() - d.getTime()) / 1000);
    if (diffSec < 60) return `${diffSec}s назад`;
    if (diffSec < 3600) return `${Math.round(diffSec / 60)}m назад`;
    if (diffSec < 86400) return `${Math.round(diffSec / 3600)}h назад`;
    return d.toLocaleDateString("ru-RU");
  } catch {
    return iso;
  }
}
